import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  AlertTriangle, Check, Copy, Download, KeyRound, Link2, Link2Off, Loader2, QrCode, ShieldCheck, Unlock, X,
} from 'lucide-react';
import { describeError } from '../../services/errorMessage';
import {
  entsperreKundenzugang,
  fetchKundenzugang,
  setzeKundenzugangPin,
  updateKundenzugang,
} from '../../services/sifa/kundenportalService';
import {
  ZUGANG_STATUS_LABEL,
  erzeugePin,
  formatZeit,
  portalUrl,
  sperrRestText,
  zugangStatus,
} from '../../services/sifa/kundenportalLogic';
import type { SifaKundenzugang } from '../../services/sifa/kundenportalTypes';

interface Props {
  projectId: string;
  isReadOnly?: boolean;
}

/**
 * Innendienst-Karte „Kundenzugang": der Betrieb bekommt EINEN Link
 * (#/kundenportal/<token>) und eine sechsstellige PIN.
 *
 * Die PIN entsteht HIER im Browser (erzeugePin), geht ueber die RPC
 * `sifa_kundenzugang_pin_setzen` als Hash in die Datenbank und wird dem
 * Innendienst GENAU EINMAL angezeigt — mit dem Hinweis, sie dem Kunden zu
 * nennen. Danach gibt es sie nirgends mehr; „Neue PIN" erzeugt eine andere.
 *
 * Alles andere (deaktivieren, Ablauf, Sperre aufheben) laeuft ueber die
 * Tabelle mit Spaltenrechten (Nr. 93); der Client sieht `pin_hash` nie.
 */
export const KundenzugangKarte: React.FC<Props> = ({ projectId, isReadOnly = false }) => {
  const [zugang, setZugang] = useState<SifaKundenzugang | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Frisch erzeugte PIN — nur bis der Dialog geschlossen wird. */
  const [neuePin, setNeuePin] = useState<string | null>(null);
  const [qrOffen, setQrOffen] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  const neuLaden = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    try {
      setZugang(await fetchKundenzugang(projectId));
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setLaden(false);
    }
  }, [projectId]);

  useEffect(() => {
    void neuLaden();
  }, [neuLaden]);

  const url = zugang ? portalUrl(window.location.origin, window.location.pathname, zugang.token) : null;
  const status = zugangStatus(zugang);
  const chip = ZUGANG_STATUS_LABEL[status];

  const aktion = async (name: string, tat: () => Promise<void>) => {
    if (isReadOnly || busy) return;
    setBusy(name);
    setFehler(null);
    try {
      await tat();
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Zugang anlegen UND sofort eine PIN vergeben — ohne PIN ist der Link tot.
   * EIN Schritt: die RPC `sifa_kundenzugang_pin_setzen` legt die Zeile an,
   * falls sie fehlt (Nr. 94). Ein Insert ueber die Tabelle und eine zweite
   * RPC hinterliessen bei einem Abbruch dazwischen eine Zeile ohne PIN, die
   * die Karte als „aktiv" zeigte. Fehlt Nr. 94 noch, meldet die RPC das im
   * Klartext — ohne sie gibt es keinen Zugang, auch keinen halben.
   */
  const anlegen = () =>
    aktion('anlegen', async () => {
      const pin = erzeugePin();
      const ok = await setzeKundenzugangPin(projectId, pin);
      if (!ok) throw new Error('Der Zugang konnte nicht angelegt werden. Bitte erneut versuchen.');
      const z = await fetchKundenzugang(projectId);
      if (!z) throw new Error('Der Zugang wurde angelegt, lässt sich aber nicht lesen. Bitte die Seite neu laden.');
      setZugang(z);
      setNeuePin(pin);
    });

  const neuePinErzeugen = () =>
    aktion('pin', async () => {
      if (!zugang) return;
      if (!window.confirm('Neue PIN erzeugen? Die bisherige PIN gilt danach nicht mehr — der Kunde braucht die neue.')) return;
      const pin = erzeugePin();
      const ok = await setzeKundenzugangPin(projectId, pin);
      if (!ok) throw new Error('Für dieses Projekt gibt es keinen Zugang mehr — bitte neu laden.');
      setZugang({ ...zugang, fehlversuche: 0, gesperrtBis: null });
      setNeuePin(pin);
    });

  const aktivSchalten = (aktiv: boolean) =>
    aktion('aktiv', async () => {
      if (!zugang) return;
      if (!aktiv && !window.confirm('Kundenzugang deaktivieren? Der Kunde kann sich dann nicht mehr anmelden. Der Link bleibt und lässt sich wieder aktivieren.')) return;
      setZugang(await updateKundenzugang(zugang.id, { aktiv }));
    });

  /**
   * „Gueltig bis" wird erst beim VERLASSEN des Feldes gespeichert, nicht bei
   * jedem Tastendruck: <input type="date"> liefert beim Tippen des Jahres
   * Zwischenwerte (0002-03-15, 0020-03-15 …), und `aktion` verwirft alle
   * Aufrufe, solange einer laeuft — der ERSTE Zwischenwert waere gelandet
   * und der Zugang „abgelaufen" (Review 09.09.). Vergangene Tage sind kein
   * Ablauf, sondern ein Tippfehler → Meldung, nichts geschrieben.
   */
  const ablaufSetzen = (wert: string) =>
    aktion('ablauf', async () => {
      if (!zugang) return;
      if (wert === ablaufWert) return;
      const heute = new Date().toISOString().slice(0, 10);
      if (wert && wert < heute) {
        setAblaufEntwurf(ablaufWert);
        throw new Error('„Gültig bis" liegt in der Vergangenheit — bitte ein Datum ab heute wählen oder das Feld leeren.');
      }
      // <input type="date"> liefert YYYY-MM-DD; Ablauf = Ende dieses Tages (Ortszeit).
      const iso = wert ? new Date(`${wert}T23:59:59`).toISOString() : null;
      setZugang(await updateKundenzugang(zugang.id, { aktivBis: iso }));
    });

  const entsperren = () =>
    aktion('entsperren', async () => {
      if (!zugang) return;
      setZugang(await entsperreKundenzugang(zugang.id));
    });

  const kopieren = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 2000);
    } catch {
      window.prompt('Link kopieren:', url);
    }
  };

  const knopf = 'min-h-[44px] px-3 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40';
  const ablaufWert = zugang?.aktivBis ? new Date(zugang.aktivBis).toISOString().slice(0, 10) : '';
  const [ablaufEntwurf, setAblaufEntwurf] = useState<string | null>(null);
  // Der Entwurf gilt nur, solange das Feld bearbeitet wird; danach zeigt es den gespeicherten Stand.
  const ablaufAnzeige = ablaufEntwurf ?? ablaufWert;
  const sperre = sperrRestText(zugang?.gesperrtBis);

  return (
    <section data-glass-view="sifa-karte" className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-100 w-11 h-11 rounded-xl flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-sky-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-slate-800">Kundenzugang</h3>
            <p className="text-[11px] text-slate-500">Der Betrieb pflegt Mitarbeiter, sieht Unterweisungen, Dokumente und Fristen — per Link und PIN, ohne Anmeldung.</p>
          </div>
        </div>
        {!laden && (
          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 text-[11px] font-bold border rounded-full ${chip.klasse}`}>{chip.label}</span>
        )}
      </div>

      {fehler && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{fehler}</span>
        </div>
      )}

      {laden ? (
        <div className="p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-500" /></div>
      ) : !zugang ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Noch kein Zugang. „Zugang anlegen" erzeugt Link und PIN; die PIN wird einmalig angezeigt.</p>
          <button
            onClick={() => void anlegen()}
            disabled={isReadOnly || !!busy}
            title={isReadOnly ? 'Nur-Ansicht: kein Zugang anlegen' : undefined}
            className={`${knopf} text-white bg-sky-600 hover:bg-sky-700`}
          >
            {busy === 'anlegen' ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Zugang anlegen
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Letzter Zugriff</p>
              <p className="font-semibold text-slate-800">{formatZeit(zugang.letzterZugriff)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Fehlversuche</p>
              <p className={`font-semibold ${zugang.fehlversuche > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                {zugang.fehlversuche}{sperre ? ` · gesperrt ${sperre}` : ''}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 col-span-2">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 block">Gültig bis (leer = unbefristet)</label>
              <input
                type="date"
                value={ablaufAnzeige}
                onChange={(e) => setAblaufEntwurf(e.target.value)}
                onBlur={() => { const w = ablaufEntwurf; setAblaufEntwurf(null); if (w !== null) void ablaufSetzen(w); }}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                disabled={isReadOnly || !!busy}
                className="mt-0.5 w-full bg-transparent text-sm font-semibold text-slate-800 outline-none disabled:text-slate-500"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => void kopieren()} className={`${knopf} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
              {kopiert ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {kopiert ? 'Kopiert' : 'Link kopieren'}
            </button>
            <button onClick={() => setQrOffen(true)} className={`${knopf} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
              <QrCode className="w-4 h-4" /> QR anzeigen
            </button>
            <button onClick={() => void neuePinErzeugen()} disabled={isReadOnly || !!busy} className={`${knopf} text-sky-700 bg-sky-50 hover:bg-sky-100`}>
              {busy === 'pin' ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Neue PIN
            </button>
            {(zugang.fehlversuche > 0 || sperre) && (
              <button onClick={() => void entsperren()} disabled={isReadOnly || !!busy} className={`${knopf} text-amber-700 bg-amber-50 hover:bg-amber-100`}>
                {busy === 'entsperren' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Sperre aufheben
              </button>
            )}
            <button
              onClick={() => void aktivSchalten(!zugang.aktiv)}
              disabled={isReadOnly || !!busy}
              className={`${knopf} ${zugang.aktiv ? 'text-slate-600 bg-slate-100 hover:bg-slate-200' : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'}`}
            >
              {busy === 'aktiv' ? <Loader2 className="w-4 h-4 animate-spin" /> : zugang.aktiv ? <Link2Off className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
              {zugang.aktiv ? 'Deaktivieren' : 'Aktivieren'}
            </button>
          </div>

          {url && <p className="text-[10px] text-slate-400 break-all select-all" title="Kundenlink">{url}</p>}
        </div>
      )}

      {neuePin && url && <PinDialog pin={neuePin} url={url} onClose={() => setNeuePin(null)} />}
      {qrOffen && url && <QrDialog url={url} onClose={() => setQrOffen(false)} />}
    </section>
  );
};

/** Die PIN, genau einmal: gross, kopierbar, mit dem Hinweis, dass sie nicht gespeichert wird. */
const PinDialog: React.FC<{ pin: string; url: string; onClose: () => void }> = ({ pin, url, onClose }) => {
  const [kopiert, setKopiert] = useState(false);
  const text = `Kundenbereich: ${url}\nPIN: ${pin}`;
  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 2000);
    } catch {
      window.prompt('Zugangsdaten kopieren:', text);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-bold text-slate-800">Neue PIN für den Kundenzugang</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Schließen"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <p className="text-4xl font-black tracking-[0.35em] text-center text-slate-900 select-all py-2" aria-label={`PIN ${pin.split('').join(' ')}`}>{pin}</p>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <b>Jetzt notieren und dem Kunden nennen.</b> Die PIN wird nicht gespeichert und nach dem Schließen nicht mehr angezeigt.
            Bei Verlust einfach „Neue PIN" erzeugen.
          </span>
        </div>
        <p className="text-[11px] text-slate-500 break-all">Link: <span className="select-all">{url}</span></p>
        <div className="flex justify-end gap-2">
          <button onClick={() => void kopieren()} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl">
            {kopiert ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} Link + PIN kopieren
          </button>
          <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-bold text-white bg-sky-600 hover:bg-sky-700 rounded-xl">Notiert</button>
        </div>
      </div>
    </div>
  );
};

/** QR-Code zum Kundenlink (Muster UnterweisungListe.QrDialog, ohne Aushang). */
const QrDialog: React.FC<{ url: string; onClose: () => void }> = ({ url, onClose }) => {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    QRCode.toDataURL(url, { width: 480, margin: 2 })
      .then((d) => { if (aktiv) setDataUrl(d); })
      .catch((e) => { if (aktiv) setFehler(String(e?.message || e)); });
    return () => { aktiv = false; };
  }, [url]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-bold text-slate-800">QR-Code zum Kundenbereich</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Schließen"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        {fehler ? (
          <p className="text-xs text-red-700">{fehler}</p>
        ) : dataUrl ? (
          <img src={dataUrl} alt="QR-Code" className="w-full max-w-[240px] mx-auto rounded-lg border border-slate-200" />
        ) : (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-500" /></div>
        )}
        <p className="text-[10px] text-slate-400 break-all select-all text-center">{url}</p>
        <p className="text-[11px] text-slate-500 text-center">Die PIN steht nicht im Code — sie wird getrennt genannt.</p>
        {dataUrl && (
          <a
            href={dataUrl}
            download="QR_Kundenzugang.png"
            className="min-h-[40px] w-full flex items-center justify-center gap-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl"
          >
            <Download className="w-4 h-4" /> QR als PNG
          </a>
        )}
      </div>
    </div>
  );
};
